import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCreateReceita, type CreateReceitaInput } from '@/lib/hooks/receitas';

interface FormValues {
  competencia: string;
  fonte: string;
  descricao: string;
  valor: string;
  dataRecebimento: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCompetencia?: string;
}

export function ReceitaDrawer({ workspaceId, open, onOpenChange, defaultCompetencia }: Props) {
  const createMut = useCreateReceita(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      competencia: defaultCompetencia ?? '',
      fonte: '',
      descricao: '',
      valor: '',
      dataRecebimento: '',
    },
  });

  useEffect(() => {
    if (!open) {
      reset({
        competencia: defaultCompetencia ?? '',
        fonte: '',
        descricao: '',
        valor: '',
        dataRecebimento: '',
      });
    }
  }, [open, defaultCompetencia, reset]);

  async function onSubmit(values: FormValues) {
    const payload: CreateReceitaInput = {
      competencia: values.competencia,
      fonte: values.fonte,
      valor: values.valor.replace(',', '.'),
      dataRecebimento: values.dataRecebimento,
    };
    if (values.descricao) payload.descricao = values.descricao;
    try {
      await createMut.mutateAsync(payload);
      toast.success('Receita cadastrada.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar receita.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar receita"
            description="Recursos recebidos pela entidade no mês"
          />
          <SheetBody className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="competencia" className="mb-1.5 block">Competência</Label>
                <Input
                  id="competencia"
                  placeholder="2026-05"
                  {...register('competencia', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d{4}-\d{2}$/, message: 'Formato YYYY-MM' },
                  })}
                />
                {errors.competencia && (
                  <p className="text-[11px] text-warn mt-1">{errors.competencia.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="dataRecebimento" className="mb-1.5 block">Data de recebimento</Label>
                <Input
                  id="dataRecebimento"
                  type="date"
                  {...register('dataRecebimento', { required: 'Obrigatório' })}
                />
                {errors.dataRecebimento && (
                  <p className="text-[11px] text-warn mt-1">{errors.dataRecebimento.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="fonte" className="mb-1.5 block">Fonte</Label>
              <Input
                id="fonte"
                placeholder="Ex.: Prefeitura de Suzano · termo nº 123/2026"
                {...register('fonte', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.fonte && (
                <p className="text-[11px] text-warn mt-1">{errors.fonte.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="valor" className="mb-1.5 block">Valor (R$)</Label>
              <Input
                id="valor"
                inputMode="decimal"
                placeholder="0.00"
                {...register('valor', {
                  required: 'Obrigatório',
                  pattern: { value: /^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                })}
              />
              {errors.valor && (
                <p className="text-[11px] text-warn mt-1">{errors.valor.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="descricao" className="mb-1.5 block">Descrição (opcional)</Label>
              <Textarea
                id="descricao"
                placeholder="Detalhes sobre a parcela, nota de empenho, etc."
                {...register('descricao')}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar receita'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
